import React, { useCallback } from 'react';

import {
  AutocompleteOptionsDropdown,
  GooglePrediction,
} from '@rbilabs/components-library/build/components/autocomplete-options-dropdown';
import { ITextInputProps } from '@rbilabs/components-library/build/components/text-input';
import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { LocationDetails } from 'components/location-details';
import { useAddressPredictions } from 'hooks/geolocation/use-address-predictions';
import { useLocationPredictions } from 'hooks/geolocation/use-location-predictions';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { GenericPrediction } from './types';

type BaseInputProps = Pick<ITextInputProps, 'value' | 'className'>;

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  'data-testid': string;
  autoComplete?: string;
  autoFocus?: boolean;
  label: string;
  matchTypes?: ('address' | '(regions)' | 'geocode' | 'establishment' | '(cities)')[];
  required: boolean;
  eventSource?: CustomEventNames;

  message?: string;
  setMessage?: React.Dispatch<React.SetStateAction<string>>;
  predictions?: GooglePrediction[];
  onAddressSelected: (address: string, placeId?: string) => void;
  onBlur?: VoidFunction;
  onInputValueChange: React.Dispatch<React.SetStateAction<string>>;
  shouldExpandPostalCodeToAddresses?: boolean;
  isDescriptionWithMainText?: boolean;
}

interface DropdownSelection {
  address: string;
  placeId: string;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps
>(
  (
    {
      autoComplete,
      'data-testid': testId,
      eventSource,
      label,
      matchTypes = [],
      message,
      onAddressSelected = noop,
      onBlur,
      onInputValueChange,
      predictions,
      shouldExpandPostalCodeToAddresses,
      setMessage,
      value,
      isDescriptionWithMainText,
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const cdp = useCdpContext();
    const enableMapFallBack = useFlag(LaunchDarklyFlag.ENABLE_MAP_FALLBACK);

    const handleDropdownValueSelected = useCallback(
      ({ address, placeId }: DropdownSelection) => {
        onAddressSelected(address, placeId);
        // added this restriction to avoid recording the input box clear event by mistake
        // because when clearing the input box, this event will be called once
        // the root reason is that we use useCombobox reset() in this component, which "reset" the select to default
        if (address) {
          cdp.trackEvent({
            name: CustomEventNames.MANUAL_LOCATION_SEARCH,
            type: EventTypes.Search,
            attributes: {
              searchInput: value,
              selectedAddress: address.toString(),
              Name: eventSource ?? CustomEventNames.DELIVERY_ADDRESS,
            },
          });
        }
      },
      [cdp, onAddressSelected, value]
    );

    const predictionsWithNoFallback = useLocationPredictions(value, {
      eventSource: eventSource ?? CustomEventNames.DELIVERY_ADDRESS,
      includeExactMatch: true,
      matchTypes,
      shouldExpandPostalCodeToAddresses,
      skip: enableMapFallBack,
    })
      ?.slice(0, 5)
      ?.map((prediction: GenericPrediction) => ({
        id: prediction.place_id,
        description: isDescriptionWithMainText
          ? prediction.structured_formatting.main_text
          : prediction.description,
        mainText: prediction.structured_formatting.main_text,
        secondaryText: prediction.structured_formatting.secondary_text,
      }));

    const { predictions: predictionsFromTomtom } = useAddressPredictions({
      pieceOfAddress: value,
      skip: !enableMapFallBack,
    });

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [setMessage, onInputValueChange]
    );

    return (
      <>
        <AutocompleteOptionsDropdown
          autoComplete={autoComplete}
          noPredictionsFoundText={formatMessage({ id: 'noLocationsMatchYourSearch' })}
          data-testid={testId}
          onSelection={handleDropdownValueSelected}
          predictions={
            enableMapFallBack ? (predictionsFromTomtom ?? predictions) : predictionsWithNoFallback
          }
          predictionRenderer={LocationDetails}
          onBlur={onBlur}
          onInputChange={handleInputValueChange}
          value={value}
          label={label}
          errorMessage={message}
          ref={ref}
          clearTextIconTitle={formatMessage({ id: 'clearText' })}
        />
      </>
    );
  }
);
